import React, { useState } from 'react';
import { getIslandCapabilities } from 'alakai-aloha-shared';
import { useItineraryOffer } from 'alakai-aloha-shared/public-ui';
import { Link } from 'react-router-dom';
import { PREVIEW_PRICE_DISPLAY } from '../../config/pricing';
import { getRuntimeSiteContext } from '../../lib/siteContext';

const StepIcon = ({ name, className = '' }) => {
  switch (name) {
    case 'interview':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          className={className}
          aria-hidden="true"
        >
          <rect
            x="4"
            y="3"
            width="16"
            height="18"
            rx="2"
            ry="2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="M9 7h6M9 11h6M9 15h3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      );
    case 'brain':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          className={className}
          aria-hidden="true"
        >
          <path
            d="M9.5 4.5a2.5 2.5 0 00-5 0v4.25A2.75 2.75 0 003 11.5c0 1.27.84 2.33 2 2.66V15a3 3 0 003 3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M14.5 4.5a2.5 2.5 0 015 0v4.25A2.75 2.75 0 0121 11.5c0 1.27-.84 2.33-2 2.66V15a3 3 0 01-3 3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M9.5 7.5h5m-5 3h5m-2.5 3h2.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      );
    case 'calendar':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          className={className}
          aria-hidden="true"
        >
          <rect
            x="3"
            y="4"
            width="18"
            height="16"
            rx="2"
            ry="2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="M16 2v4M8 2v4M3 10h18"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            d="M9 14h2v2H9z"
            fill="currentColor"
          />
        </svg>
      );
    default:
      return null;
  }
};

const ItineraryWorkflow = ({
  primaryCtaLabel,
  primaryCtaHref,
  onPrimaryCtaClick,
  ctaLocation = 'workflow_primary',
  showExamplesLink,
}) => {
  const { island, islandConfig } = React.useMemo(() => getRuntimeSiteContext(), []);
  const capabilities = React.useMemo(() => getIslandCapabilities(island), [island]);
  const { active: offerActive } = useItineraryOffer();
  const pilotActive = offerActive && capabilities.tripPlanner;
  const [selectedStep, setSelectedStep] = useState(1);
  const shouldShowExamplesLink = showExamplesLink ?? capabilities.featuredItineraries;
  const resolvedPrimaryCtaLabel =
    (pilotActive ? 'Plan My Trip Free' : primaryCtaLabel) ||
    (capabilities.tripPlanner ? 'Start the Interview' : `${islandConfig.shortTitle} Planner Coming Soon`);
  const resolvedPrimaryCtaHref =
    primaryCtaHref || (capabilities.tripPlanner ? '/interview' : '/coming-soon');

  const steps = [
    {
      id: 1,
      number: '1',
      label: 'Interview',
      icon: 'interview',
      title: 'Interview',
      content: [
        "We start by getting to know you—who you're traveling with, when you're going, and what kind of vibe you want. Our quick (and fun!) interview takes ~10 minutes and helps us tailor every detail to your travel style, priorities, and pace."
      ],
    },
    {
      id: 2,
      number: '2',
      label: 'Get a Preview',
      icon: 'brain',
      title: 'Get a Preview',
      content: [
        'Our AI-powered itinerary engine uses your interview answers to craft an ideal mix of activities and dining. See a sneak peek of your personalized plan with a free preview of the first day and a half of your trip.'
      ],
    },
    {
      id: 3,
      number: '3',
      label: pilotActive ? 'Get Your Full Trip Free' : `Unlock for ${PREVIEW_PRICE_DISPLAY}`,
      icon: 'calendar',
      title: pilotActive ? 'Get Your Full Trip Free' : `Unlock for ${PREVIEW_PRICE_DISPLAY}`,
      content: [
        pilotActive
          ? 'Your complete itinerary is free during our 30-day pilot. No credit card required. After your preview, get the rest of your personalized trip, with a printable PDF, calendar export, and sharing.'
          : 'Unlock the rest of your itinerary and maximize those precious vacation days. For the cost of a couple coconut cocktails, we fine-tune each day around your pace, tastes, and preferences. No generic templates, just your version of paradise.'
      ],
    },
  ];

  return (
    <section className="text-center">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">How It Works</h2>
        <p className="text-lg md:text-xl mb-2 max-w-3xl mx-auto leading-relaxed text-center">
          We combine your preferences with deep local knowledge to create personalized itineraries for the...
        </p>
        <p className="text-lg md:text-xl max-w-3xl mb-4 mx-auto leading-relaxed text-center font-semibold italic">
          Best. Trip. Ever.
        </p>

        {/* Mobile horizontal tabs (visible on small screens) */}
        <div className="lg:hidden mb-4">
          <div className="flex items-center bg-white/5 rounded-lg p-1 border border-white/10 sm:overflow-x-auto sm:scroll-smooth sm:snap-x sm:snap-mandatory sm:overscroll-x-contain">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => setSelectedStep(step.id)}
                  className={`flex-1 sm:shrink-0 sm:min-w-[160px] sm:snap-start flex items-center justify-center gap-2 p-2 rounded-md transition-all ${
                    selectedStep === step.id
                      ? 'bg-white/10 text-white'
                      : 'text-white/70 hover:bg-white/5'
                  }`}
                >
                  <span className="inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-white/10">
                    <StepIcon
                      name={step.icon}
                      className="h-4 w-4 text-emerald-300"
                    />
                  </span>
                  <span className="text-xl font-bold">
                    {step.number}
                  </span>
                  <span className="text-sm uppercase tracking-wide hidden sm:inline">{step.label}</span>
                </button>
                {/* Arrow connector between tabs */}
                {index < steps.length - 1 && (
                  <div className="px-2 hidden sm:block">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-white/40">
                      <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Desktop two-column layout + Mobile content */}
        <div className="flex flex-col lg:flex-row gap-8 text-left">
          {/* Left column - Step selector (desktop only) */}
          <div className="hidden lg:block lg:w-1/3">
            <div className="space-y-4">
              {steps.map((step) => (
                <div
                  key={step.id}
                  onClick={() => setSelectedStep(step.id)}
                  className={`p-4 rounded-lg cursor-pointer transition-all ${
                    selectedStep === step.id
                      ? 'bg-white/10 border-l-4 border-white'
                      : 'hover:bg-white/5'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5">
                      <StepIcon
                        name={step.icon}
                        className="h-5 w-5 text-emerald-300"
                      />
                    </span>
                    <div>
                      <div className="text-sm opacity-70">Step {step.number}</div>
                      <div className={`text-lg ${selectedStep === step.id ? 'font-bold' : 'font-medium'}`}>
                        {step.label}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column - Content details */}
          <div className="w-full lg:w-2/3">
            <div className="bg-white/5 rounded-lg p-6 lg:p-8 border border-white/10">
              {steps.map((step) => (
                selectedStep === step.id && (
                  <div key={step.id}>
                    <div className="mb-4 flex items-center gap-3">
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5">
                        <StepIcon
                          name={step.icon}
                          className="h-5 w-5 text-emerald-300"
                        />
                      </span>
                      <h3 className="text-xl md:text-2xl font-bold">
                        {step.title}
                      </h3>
                    </div>
                    <div className="space-y-4">
                      {step.content.map((paragraph, index) => (
                        <p key={index} className="text-base md:text-lg leading-relaxed opacity-90">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="mt-6 flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:justify-center lg:gap-4 max-w-3xl mx-auto">
          {shouldShowExamplesLink ? (
            <Link
              to="/featured"
              className="btn-secondary w-full lg:flex-1 text-center"
            >
              See Examples
            </Link>
          ) : null}
          {onPrimaryCtaClick ? (
            <button
              type="button"
              className="btn-primary-cta w-full lg:flex-1 text-center"
              onClick={() => onPrimaryCtaClick(ctaLocation, resolvedPrimaryCtaLabel)}
            >
              {resolvedPrimaryCtaLabel}
            </button>
          ) : (
            <Link
              to={resolvedPrimaryCtaHref}
              className="btn-primary-cta w-full lg:flex-1 text-center"
            >
              {resolvedPrimaryCtaLabel}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export default ItineraryWorkflow;
